import React, {useEffect, useState, useCallback, useMemo} from "react";
import {useSBLDashboard} from "./context/SBLDashboardContext";
import {FormattedMessage} from "react-intl";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Select, {SelectOption} from "@amzn/meridian/select";
import Checkbox from "@amzn/meridian/checkbox";
import LoaderView from "../../loader/Loader";
import {
    BLUE_COLOR,
    ORANGE_COLOR,
    LOW_BATTERY_THRESHOLD,
    ANDON_COLORS,
    TIMER_OPTIONS,
    DEFAULT_DURATION,
    LIGHT_COMMAND_ON,
    LIGHT_COMMAND_OFF,
    ASCENDING,
    EMPTY_STRING_PLACEHOLDER,
    SBL_DASHBOARD_ACTIONS,
    BATTERY_COLUMN
} from "../../util/Constants";
import CartFilterPanel from "../ledcontrollerstag/CartFilterPanel";
import {applyPercentileFilter} from "../ledcontrollerstag/CartFilterUtils";
import {sendOnCommand, sendOffCommand} from "../ledcontrollerstag/SBLCommandUtils";
import {initStationConfig, fetchSBLDashboardData} from "./network/SBLDashboardNetworkAPI";

const getBatteryColor = (battery) => {
    const value = parseInt(battery, 10);
    if (isNaN(value)) return ORANGE_COLOR;
    return value < LOW_BATTERY_THRESHOLD ? ORANGE_COLOR : BLUE_COLOR;
};

const SBLDashboard = () => {
    const {viewModel, dispatch} = useSBLDashboard();
    const [isFiltered, setIsFiltered] = useState(false);
    const [selectedCarts, setSelectedCarts] = useState(new Set());
    const [color, setColor] = useState(ANDON_COLORS[0].value);
    const [duration, setDuration] = useState(DEFAULT_DURATION);
    const [sending, setSending] = useState(false);
    const [commandResult, setCommandResult] = useState(null);

    useEffect(() => {
        dispatch({type: SBL_DASHBOARD_ACTIONS.LOAD_VIEW});
        initStationConfig(dispatch);
        fetchSBLDashboardData(dispatch);
    }, [dispatch]);

    const onSort = useCallback((sortDetails) => {
        dispatch({type: SBL_DASHBOARD_ACTIONS.SORT_DATA, data: {column: sortDetails.sortColumn, direction: sortDetails.sortDirection}});
    }, [dispatch]);

    const {filteredData, filteredIds, allSelected} = useMemo(() => {
        const allData = viewModel.resourceDynamicAttributes || [];
        const displayData = isFiltered ? applyPercentileFilter(allData, viewModel.percentileThreshold) : allData;
        const {column, direction} = viewModel.sortPref;
        const sorted = [...displayData].sort((a, b) => {
            let valA, valB;
            if (column === BATTERY_COLUMN) {
                valA = parseInt((a.dynamicAttributes || {})[BATTERY_COLUMN], 10) || 0;
                valB = parseInt((b.dynamicAttributes || {})[BATTERY_COLUMN], 10) || 0;
            } else {
                valA = a[column] || "";
                valB = b[column] || "";
            }
            if (valA < valB) return direction === ASCENDING ? -1 : 1;
            if (valA > valB) return direction === ASCENDING ? 1 : -1;
            return 0;
        });
        const ids = sorted.map(d => d.scannableId);
        const allSel = ids.length > 0 && ids.every(id => selectedCarts.has(id));
        return {filteredData: sorted, filteredIds: ids, allSelected: allSel};
    }, [viewModel.resourceDynamicAttributes, viewModel.percentileThreshold, viewModel.sortPref, isFiltered, selectedCarts]);

    const handleSendOn = useCallback(async () => {
        const ids = Array.from(selectedCarts);
        if (ids.length === 0) return;
        setSending(true);
        setCommandResult(null);
        try {
            const result = await sendOnCommand(ids, color, duration);
            setCommandResult({command: LIGHT_COMMAND_ON, ...result});
        } catch (e) {
            setCommandResult({command: LIGHT_COMMAND_ON, succeeded: 0, failed: ids.length, total: ids.length});
        } finally {
            setSending(false);
        }
    }, [selectedCarts, color, duration]);

    const handleSendOff = useCallback(async () => {
        const ids = Array.from(selectedCarts);
        if (ids.length === 0) return;
        setSending(true);
        setCommandResult(null);
        try {
            const result = await sendOffCommand(ids);
            setCommandResult({command: LIGHT_COMMAND_OFF, ...result});
        } catch (e) {
            setCommandResult({command: LIGHT_COMMAND_OFF, succeeded: 0, failed: ids.length, total: ids.length});
        } finally {
            setSending(false);
        }
    }, [selectedCarts]);

    const toggleCartSelection = useCallback((scannableId) => {
        setSelectedCarts(prev => {
            const next = new Set(prev);
            if (next.has(scannableId)) next.delete(scannableId);
            else next.add(scannableId);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback((ids) => {
        setSelectedCarts(prev => {
            const allSel = ids.every(id => prev.has(id));
            if (allSel) return new Set();
            return new Set(ids);
        });
    }, []);

    const allData = viewModel.resourceDynamicAttributes || [];
    const percentile = viewModel.percentileThreshold;

    return (
        <Row widths="100%" alignmentVertical="top" spacingInset="none none large none">
            <Column spacing="medium">
                {viewModel.loading ? <LoaderView/> : (
                    <React.Fragment>
                        {viewModel.error && (
                            <Alert type="error" size="small">
                                {viewModel.error}
                            </Alert>
                        )}
                        <CartFilterPanel
                            percentileThreshold={percentile}
                            isFiltered={isFiltered}
                            onToggleFilter={() => setIsFiltered(prev => !prev)}
                            matchCount={filteredData.length}
                            totalCount={allData.length}
                        />

                        {commandResult && (
                            <Alert
                                type={commandResult.failed === 0 ? "success" : "warning"}
                                size="small"
                                onClose={() => setCommandResult(null)}
                            >
                                {commandResult.command} command: {commandResult.succeeded}/{commandResult.total} succeeded
                                {commandResult.failed > 0 && `, ${commandResult.failed} failed`}
                            </Alert>
                        )}

                        <Row alignmentVertical="bottom" spacing="small">
                            <Column spacing="xsmall">
                                <Text type="b200">Color</Text>
                                <Select value={color} onChange={setColor} size="small">
                                    {ANDON_COLORS.map(c => (
                                        <SelectOption key={c.value} value={c.value} label={c.label}/>
                                    ))}
                                </Select>
                            </Column>
                            <Column spacing="xsmall">
                                <Text type="b200">Timer</Text>
                                <Select value={duration} onChange={setDuration} size="small">
                                    {TIMER_OPTIONS.map(t => (
                                        <SelectOption key={t.value} value={t.value} label={t.label}/>
                                    ))}
                                </Select>
                            </Column>
                            <Button type="primary" size="small" onClick={handleSendOn} disabled={sending || selectedCarts.size === 0}>
                                Send ON ({selectedCarts.size})
                            </Button>
                            <Button type="secondary" size="small" onClick={handleSendOff} disabled={sending || selectedCarts.size === 0}>
                                Send OFF ({selectedCarts.size})
                            </Button>
                            {selectedCarts.size > 0 && (
                                <Button type="tertiary" size="small" onClick={() => setSelectedCarts(new Set())}>
                                    Clear Selection
                                </Button>
                            )}
                        </Row>

                        {filteredData.length === 0 ?
                            <Alert type="informational" size="small">
                                <FormattedMessage id="sbl.dashboard.nodata" defaultMessage="No data available."/>
                            </Alert> :
                            <Table
                                headerRows={1}
                                spacing="small"
                                sortColumn={viewModel.sortPref.column}
                                sortDirection={viewModel.sortPref.direction}
                                onSort={onSort}
                                showDividers={true}
                            >
                                <TableRow highlightOnHover={false}>
                                    <TableCell>
                                        <Checkbox
                                            checked={allSelected}
                                            onChange={() => toggleSelectAll(filteredIds)}
                                        />
                                    </TableCell>
                                    <TableCell sortColumn="scannableId">Cart ID</TableCell>
                                    <TableCell sortColumn={BATTERY_COLUMN}>Battery (%)</TableCell>
                                    <TableCell>Dev EUI</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Version</TableCell>
                                    <TableCell>Is Charging</TableCell>
                                </TableRow>
                                {filteredData.map((item) => {
                                    const attrs = item.dynamicAttributes || {};
                                    const batteryColor = getBatteryColor(attrs[BATTERY_COLUMN]);
                                    return (
                                        <TableRow key={item.scannableId} highlightOnHover={true}>
                                            <TableCell>
                                                <Checkbox
                                                    checked={selectedCarts.has(item.scannableId)}
                                                    onChange={() => toggleCartSelection(item.scannableId)}
                                                />
                                            </TableCell>
                                            <TableCell><b>{item.scannableId}</b></TableCell>
                                            <TableCell>
                                                <b style={{color: batteryColor}}>
                                                    {attrs[BATTERY_COLUMN] || EMPTY_STRING_PLACEHOLDER}
                                                </b>
                                            </TableCell>
                                            <TableCell>{attrs.dev_eui || EMPTY_STRING_PLACEHOLDER}</TableCell>
                                            <TableCell>{attrs.type || EMPTY_STRING_PLACEHOLDER}</TableCell>
                                            <TableCell>{attrs.version || EMPTY_STRING_PLACEHOLDER}</TableCell>
                                            <TableCell>{attrs.is_charging || EMPTY_STRING_PLACEHOLDER}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </Table>
                        }
                    </React.Fragment>
                )}
            </Column>
        </Row>
    );
};

export default SBLDashboard;



// WEBPACK FOOTER //
// ./src/components/devicemanagement/ledcontroller/SBLDashboard.js